import React, { useMemo } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Avatar, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { QuickViewDeleteIcon, QuickViewEditIcon } from "assets/material-custom-icons";
import { Menu } from "components/popups";
import { ICharContact } from "store/types";
import { IContactDealRole } from "store/types/user-management";

interface Props {
  contact: ICharContact;
  roleOptions: IContactDealRole[];
  disabled?: boolean;
  // Server validation error for this contact — highlights the card and shows the message.
  error?: string;
  onChangeRole: (id: number | string, role: string) => void;
  onEdit: (contact: ICharContact) => void;
  onRemove: (contact: ICharContact) => void;
}

const displayName = (contact: ICharContact): string =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim();

const initial = (contact: ICharContact): string =>
  (displayName(contact) || contact.email || "").trim().charAt(0).toUpperCase() || "?";

// Compact contact row for the deal characteristics "Contact" tab: avatar + name + email, a Role
// dropdown, and a ⋮ menu (Edit / Remove from deal).
export const ContactCard: React.FC<Props> = ({
  contact,
  roleOptions,
  disabled,
  error,
  onChangeRole,
  onEdit,
  onRemove
}) => {
  const name = displayName(contact);
  const role = contact.role ?? ""; // holds the deal_role key

  // Render label by key; keep an already-saved role selectable even if it's missing from the list.
  const options = useMemo(() => {
    if (role && !roleOptions.some((option) => option.key === role)) {
      return [...roleOptions, { key: role, label: role }];
    }
    return roleOptions;
  }, [roleOptions, role]);

  const roleLabel = useMemo(() => options.find((option) => option.key === role)?.label ?? "", [options, role]);

  const menuOptions = [
    { title: "Edit Contact", icon: <QuickViewEditIcon />, onClick: () => onEdit(contact) },
    { title: "Remove from Deal", icon: <QuickViewDeleteIcon />, onClick: () => onRemove(contact) }
  ];

  return (
    <Stack sx={{ gap: "4px" }}>
      <Stack direction="row" sx={{ width: "100%", gap: "8px", alignItems: "center" }}>
        <Stack direction="row" sx={{ flex: "1 0 0", minWidth: 0, gap: "8px", alignItems: "center" }}>
        <Avatar
          sx={{ width: 40, height: 40, bgcolor: "#ede5f9", color: "#6e45eb", fontSize: "18px", fontWeight: 500 }}
        >
          {initial(contact)}
        </Avatar>
        <Stack sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: "16px", fontWeight: 700, color: "#071f35" }}>
            {name || "New contact"}
          </Typography>
          {contact.email && (
            <Typography noWrap sx={{ fontSize: "14px", lineHeight: "16px", color: "#496682" }}>
              {contact.email}
            </Typography>
          )}
        </Stack>
      </Stack>

      <TextField
        select
        size="small"
        label="Role"
        value={role}
        onChange={(event) => contact.id != null && onChangeRole(contact.id, event.target.value)}
        disabled={disabled}
        sx={{
          width: "140px",
          flexShrink: 0,
          "& .MuiOutlinedInput-root": { height: "32px" },
          "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0 }
        }}
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          displayEmpty: true,
          renderValue: () =>
            role ? (
              roleLabel
            ) : (
              <Typography component="span" sx={{ color: "#92a7bc", fontSize: "14px" }}>
                Select
              </Typography>
            )
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.key} value={option.key}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

        {!disabled && <Menu options={menuOptions} iconButtonProps={{ sx: { p: "4px" } }} />}
      </Stack>
      {error && (
        <Stack direction="row" sx={{ alignItems: "center", gap: "4px", pl: "48px" }}>
          <ErrorOutlineIcon sx={{ fontSize: "14px", color: "#d32f2f" }} />
          <Typography sx={{ fontSize: "12px", color: "#d32f2f" }}>{error}</Typography>
        </Stack>
      )}
    </Stack>
  );
};
